import { motion } from 'framer-motion';
import { Play, ArrowUpRight } from 'lucide-react';
import { FEATURED_FILM, REEL } from './data';

// HOME — full-bleed cinema poster hero, with letterbox bars top/bottom (deliberate).
// Below: a numbered film index (NOT a 3-tab grid).
export default function Home({ setPage }) {
  return (
    <div className="bg-[#0A0908] text-[#FBF6E8]">
      {/* Cinema poster hero, with deliberate letterbox bars */}
      <section className="relative">
        {/* Top letterbox bar */}
        <div className="bg-[#0A0908] h-10 border-b border-[#FBF6E8]/10 flex items-center justify-between px-6 lg:px-12 font-mono text-[9px] tracking-[0.3em] uppercase text-[#FBF6E8]/45">
          <span>Pelagic Reel · 2025 / 2026</span>
          <span className="text-[#C9963E]">CINEMASCOPE 2.39 : 1</span>
        </div>
        <div className="relative aspect-[2.39/1] min-h-[480px] overflow-hidden">
          <img src={FEATURED_FILM.poster} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/30 to-[#0A0908]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0908] via-transparent to-transparent" />
          <div className="absolute bottom-12 left-6 lg:left-12 max-w-3xl">
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#C9963E] mb-4">— Now playing</p>
            <h1 className="font-serif text-[40px] sm:text-[64px] lg:text-[100px] leading-[0.92] tracking-[-0.025em]">{FEATURED_FILM.title}</h1>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FBF6E8]/65 mt-4">{FEATURED_FILM.client} · {FEATURED_FILM.runtime} · Dir. {FEATURED_FILM.dir} · DOP {FEATURED_FILM.dop}</p>
            <button className="mt-8 group flex items-center gap-3 bg-[#C9963E] text-[#0A0908] px-6 py-3.5 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#FBF6E8] transition-colors" data-testid="film-home-play">
              <Play className="w-4 h-4 group-hover:scale-110 transition-transform" fill="currentColor" /> Watch the film
            </button>
          </div>
          {/* Director's mark - corner */}
          <div className="absolute top-6 right-6 lg:top-8 lg:right-12 font-mono text-[9px] tracking-[0.4em] uppercase text-[#FBF6E8]/50 text-right">
            <p>Pelagic / 2025</p>
            <p className="mt-1 text-[#C9963E]">FILM Nº 47</p>
          </div>
        </div>
        {/* Bottom letterbox bar */}
        <div className="bg-[#0A0908] h-10 border-t border-[#FBF6E8]/10 flex items-center justify-between px-6 lg:px-12 font-mono text-[9px] tracking-[0.3em] uppercase text-[#FBF6E8]/45">
          <span>04:12 / {FEATURED_FILM.runtime}</span>
          <span>Dolby Atmos · 5.1 · Color: ARRI ALEXA Mini LF</span>
        </div>
      </section>

      {/* Numbered film index — looks like a contact sheet, NOT a tab block */}
      <section className="py-24 max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#C9963E] mb-3">— Selected work</p>
            <h2 className="font-serif text-4xl lg:text-7xl leading-[0.95]">Six films, <span className="italic text-[#C9963E]">in rotation</span>.</h2>
          </div>
          <button onClick={() => setPage('work')} className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9963E] hover:text-[#FBF6E8] flex items-center gap-2">
            Forty-seven, in total <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#FBF6E8]/10">
          {REEL.map((r, i) => (
            <motion.button key={r.title} onClick={() => setPage('work')} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="group bg-[#0A0908] text-left">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={r.poster} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" />
                <div className="absolute inset-0 bg-[#0A0908]/30 group-hover:bg-[#0A0908]/15 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-[#C9963E]/95 rounded-full w-14 h-14 flex items-center justify-center">
                    <Play className="w-5 h-5 text-[#0A0908] ml-0.5" fill="currentColor" />
                  </div>
                </div>
                <span className="absolute top-3 left-3 font-mono text-[8px] tracking-[0.4em] uppercase bg-[#C9963E] text-[#0A0908] px-2 py-1">{r.type}</span>
                <span className="absolute top-3 right-3 font-mono text-[8px] tracking-[0.3em] uppercase text-[#FBF6E8]/75 bg-[#0A0908]/60 backdrop-blur-sm px-2 py-1">{r.runtime}</span>
              </div>
              <div className="p-5">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9963E] mb-1.5">No. {String(i + 1).padStart(2, '0')} · {r.client}</p>
                <p className="font-serif text-2xl leading-tight">{r.title}</p>
                <p className="font-mono text-[10px] text-[#FBF6E8]/45 mt-2">{r.festivals[0]} · {r.yr}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </div>
  );
}
